<template>
  <div class="card border-0 shadow-none rounded-3 mb-4">
    <div class="card-body p-4 p-xl-40">
      <div class="toolbar-card mb-4">
        <div class="toolbar-left">
          <h4 class="mb-1">{{ $t("group_permission_page.list_title") }}</h4>
          <p class="text-muted mb-0">
            {{ $t("group_permission_page.list_description") }}
          </p>
        </div>
        <div class="toolbar-right">
          <div class="search-shell">
            <input
              :value="searchQuery"
              type="text"
              class="form-control toolbar-search"
              :placeholder="$t('group_permission_page.search_placeholder')"
              @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            />
            <i class="ri-search-line search-icon"></i>
          </div>
          <router-link to="/create-group-permission" class="btn style-one btn-sm d-flex align-items-center gap-2">
            <i class="ri-add-circle-line"></i>
            <span>{{ $t("group_permission_page.create_button") }}</span>
          </router-link>
        </div>
      </div>

      <a-table
        :columns="columns"
        :data-source="roles"
        :row-key="'id'"
        :pagination="paginationConfig"
        :loading="loading"
        :locale="{ emptyText: $t('group_permission_page.no_roles_found') }"
        class="role-table"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'role_name'">
            <div class="role-name-cell">
              <span class="role-avatar">
                {{ getRoleInitials(record.role_name) }}
              </span>
              <div>
                <div class="role-title">{{ record.role_name || $t("group_permission_page.unnamed_role") }}</div>
                <div class="role-subtitle">{{ $t("group_permission_page.role_subtitle") }}</div>
              </div>
            </div>
          </template>
          <template v-else-if="column.key === 'description'">
            <span class="text-muted">{{ text || $t("group_permission_page.no_description") }}</span>
          </template>
          <template v-else-if="column.key === 'status'">
            <span class="status-badge" :class="Number(record.status ?? 1) === 1 ? 'status-active' : 'status-inactive'">
              {{ Number(record.status ?? 1) === 1 ? $t("group_permission_page.status_active") : $t("group_permission_page.status_inactive") }}
            </span>
          </template>
          <template v-else-if="column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2">
              <router-link :to="`/edit-group-permission/${record.id}`" class="icon-btn" :title="$t('group_permission_page.edit')">
                <i class="ri-pencil-line"></i>
              </router-link>
              <button class="icon-btn danger" :title="$t('group_permission_page.delete')" @click="$emit('delete', record)">
                <i class="ri-delete-bin-6-line"></i>
              </button>
            </div>
          </template>
          <template v-else>
            {{ text || $t("group_permission_page.not_available") }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { RolePermission } from "../../../services/role_permission";

export default defineComponent({
  name: "GroupPermission",
  emits: ["update:searchQuery", "delete"],
  props: {
    roles: {
      type: Array as PropType<RolePermission[]>,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    paginationConfig: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t } = useI18n();
    const columns = computed(() => [
      { title: t("group_permission_page.table_role"), dataIndex: "role_name", key: "role_name", align: "left" as const, width: 320 },
      { title: t("group_permission_page.table_description"), dataIndex: "description", key: "description", align: "center" as const, width: 360 },
      { title: t("group_permission_page.table_status"), dataIndex: "status", key: "status", align: "center" as const, width: 140 },
      { title: t("group_permission_page.table_action"), key: "actions", align: "center" as const, width: 140 },
    ]);

    const getRoleInitials = (name?: string) =>
      String(name || "RP")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");

    return {
      columns,
      getRoleInitials,
    };
  },
});
</script>

<style scoped>
.toolbar-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
  padding: 22px;
  border: 1px solid #edf0f7;
  border-radius: 20px;
  background: #fbfcff;
}

.toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.search-shell {
  position: relative;
  min-width: 340px;
}

.toolbar-search {
  min-height: 54px;
  padding: 14px 48px 14px 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  box-shadow: none;
}

.toolbar-search:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.search-icon {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #7d8aa2;
  pointer-events: none;
}

.role-table :deep(.ant-table-thead > tr > th) {
  font-weight: 700;
}

.role-name-cell {
  display: flex;
  align-items: center;
  gap: 14px;
}

.role-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 700;
}

.role-title {
  color: #25324b;
  font-weight: 700;
}

.role-subtitle {
  color: #7d8aa2;
  font-size: 13px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-active {
  background: #e8f7ee;
  color: #1f9254;
}

.status-inactive {
  background: #fdecec;
  color: #d33c43;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e3e8f5;
  background: #fff;
  color: #4f46e5;
}

.icon-btn.danger {
  color: #d33c43;
}
</style>
